import { Map } from 'immutable';
import { connect } from 'react-redux';
import { ThunkDispatch } from "redux-thunk";

import { NoteColor, ProcessStep } from "../constants";

import {
    deleteNote,
    noteDragging,
    setPaneSize,
    setScroll,
    showHelpContent,
    showTimeDialog,
    showUpdateNoteDialog,
    startNoteDragging,
    stopNoteDragging,
    setZoomLevel,
    startPanning,
    panning,
    stopPanning,
    Action,
} from '../actions';

import {
    getCurrentTemplate,
    getNoteDragging,
    getNotes,
    getPaneState,
    getProcessStep,
    getTemplateText,
    getTextFields,
    getZoomLevel,
    getPanning,
    DraggingState,
    HelpPanelContentType,
    Note,
    State,
    TemplateData,
} from '../reducers';

import Canvas from '../components/CanvasView';
import { PanningState } from "../reducers/ui-state";


interface StateProps {
    readonly processStep: ProcessStep,
    readonly width: number,
    readonly height: number,
    readonly notes: ReadonlyArray<Note>,
    readonly zoomLevel: number,
    readonly scrollLeft: number,
    readonly scrollTop: number,
    readonly panning: PanningState | null,
    readonly dragging: DraggingState | null,
    readonly templateData: TemplateData,
    readonly textFields: Map<string, string>,
    readonly templateTextSupplier: (id: string) => string,
}

interface DispatchProps {
    readonly onStartPanning: (mouseX: number, mouseY: number) => void,
    readonly onPanning: (mouseX: number, mouseY: number) => void,
    readonly onStopPanning: (mouseX: number, mouseY: number) => void,
    readonly onNoteStartDragging: (uuid: string, x: number, y: number) => void,
    readonly onNoteDragging: (x: number, y: number) => void,
    readonly onNoteStopDragging: (x: number, y: number) => void,
    readonly onNoteDoubleClicked: (uuid: string, text: string, color: NoteColor) => void,
    readonly onDeleteButtonClicked: (uuid: string) => void,
    readonly onBackgroundDoubleClicked: () => void,
    readonly onTemplateHelpClicked: (content: string) => void,
    readonly onTextFieldClicked: (key: string, text: string) => void,
    readonly onScrolled: (scrollLeft: number, scrollTop: number) => void,
    readonly onCanvasResized: (width: number, height: number) => void,
}


const mapStateToProps = (state: State): StateProps => {
    const currentTemplate = getCurrentTemplate(state);
    const { scrollLeft, scrollTop } = getPaneState(state);
    const zoomLevel = getZoomLevel(state);
    return (
        {
            processStep: getProcessStep(state),
            width: currentTemplate.canvasWidth,
            height: currentTemplate.canvasHeight,
            notes: getNotes(state),
            zoomLevel,
            scrollLeft,
            scrollTop,
            panning: getPanning(state),
            dragging: getNoteDragging(state),
            templateData: currentTemplate,
            textFields: getTextFields(state),
            templateTextSupplier: id => getTemplateText(state, id),
        }
    )
};

const mapDispatchToProps = (dispatch: ThunkDispatch<State, any, Action>): DispatchProps => (
    {
        onStartPanning: (mouseX: number, mouseY: number) => {
            dispatch(startPanning(mouseX, mouseY));
        },
        onPanning: (mouseX: number, mouseY: number) => {
            dispatch(panning(mouseX, mouseY));
        },
        onStopPanning: (mouseX: number, mouseY: number) => {
            dispatch(stopPanning(mouseX, mouseY));
        },
        onNoteStartDragging: (uuid: string, x: number, y: number) => {
            dispatch(startNoteDragging(uuid, x, y));
        },
        onNoteDragging: (x: number, y: number) => {
            dispatch(noteDragging(x, y));
        },
        onNoteStopDragging: (x: number, y: number) => {
            dispatch(stopNoteDragging(x, y));
        },
        onNoteDoubleClicked: (uuid: string, text: string, color: NoteColor) => {
            dispatch(showUpdateNoteDialog(uuid, text, color));
        },
        onDeleteButtonClicked: (uuid: string) => {
            dispatch(deleteNote(uuid));
        },
        onBackgroundDoubleClicked: () => {
            dispatch(setZoomLevel(0.0));
        },
        onTemplateHelpClicked: (content: string) => {
            dispatch(showHelpContent(HelpPanelContentType.TemplateState, content, 'step_structure_help_content'));
        },
        onTextFieldClicked: (key: string, text: string) => {
            dispatch(showTimeDialog(key, text));
        },
        onScrolled: (scrollLeft: number, scrollTop: number) => {
            dispatch(setScroll(scrollLeft, scrollTop));
        },
        onCanvasResized: (width: number, height: number) => {
            dispatch(setPaneSize(width, height));
        }
    }
);


export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Canvas);



// WEBPACK FOOTER //
// ./src/containers/Canvas.ts